import { getPokerMin } from '@/components/images/base64/PokerMin';
import { Bet } from '@/modules/report/slice/betRecord';
import { getPattern, updatePattern } from '@/utils/games/teenpatti';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { t } from 'i18next';

type TeenPattiResultRecordPanelProps = {
    bet: Bet;
};
export const TeenPattiResultRecordPanel = (
    props: TeenPattiResultRecordPanelProps
) => {
    const { bet } = props;

    return (
        <>
            <Stack
                direction="column"
                justifyContent="space-between"
                alignItems={'flex-end'}
            >
                <DetailResult lbl={'A'} cards={bet.ResultPlayer} />
                <DetailResult lbl={'B'} cards={bet.ResultBanker} />
            </Stack>
        </>
    );
};

export const DetailResult = ({
    lbl,
    cards,
}: {
    lbl: string;
    cards: number[];
}) => {
    return (
        <>
            <Stack direction="row" alignItems="center">
                <Grid
                    item
                    sx={{
                        marginRight: '3px',
                        marginBottom: '5px',
                    }}
                >
                    {lbl}
                </Grid>
                {cards.map(c => {
                    return (
                        <Grid key={`card-${c}`} item>
                            <Stack direction="row">
                                <Grid
                                    item
                                    sx={{
                                        aspectRatio: '31/40',
                                        zoom: '0.32',
                                        padding: '0 0.33rem',
                                    }}
                                >
                                    {getPokerMin(c)}
                                </Grid>
                            </Stack>
                        </Grid>
                    );
                })}
            </Stack>
        </>
    );
};

type ResultDisplay = {
    winResult: number;
    playerAPattern: number;
    playerBPattern: number;
    sixCardPattern: number;
    sixCardPokers: Array<number>;
    playerALightArr: Array<number>;
    playerBLightArr: Array<number>;
    sixCardLightArr: Array<number>;
};

export const TeenPattiResultDetailPanel = (
    props: TeenPattiResultRecordPanelProps
) => {
    const { bet } = props;
    const {
        winResult,
        playerAPattern,
        playerBPattern,
        sixCardPattern,
        sixCardPokers,
    } = getPattern(BigInt(bet.WinSlot));

    const resultView = {
        winResult,
        playerAPattern,
        playerBPattern,
        sixCardPattern,
        sixCardPokers,
        playerALightArr: updatePattern(bet.ResultPlayer, playerAPattern, false),
        playerBLightArr: updatePattern(bet.ResultBanker, playerBPattern, false),
        sixCardLightArr: updatePattern(sixCardPokers, sixCardPattern, true),
    };

    const SixPoker = (card: number, idx: number) => {
        return (
            <Grid
                item
                key={`six-poker-${idx}`}
                sx={{
                    aspectRatio: '31/40',
                    // zoom: '0.4',
                    zoom: '0.32',
                    opacity: getOpacity(idx),
                }}
            >
                {getPokerMin(card)};
            </Grid>
        );
    };

    const getOpacity = (index: number) => {
        if (!resultView) return 0;
        return resultView.sixCardLightArr.indexOf(index) >= 0 ? 1 : 0.4;
    };

    return (
        <Stack alignItems={'center'}>
            <Stack
                direction={'column'}
                alignItems={'center'}
                justifyContent={'flex-start'}
            >
                {(bet.ResultPlayer.length > 0 ||
                    bet.ResultBanker.length > 0) && (
                    <Grid
                        container
                        sx={{
                            direction: 'row',
                            justifyContent: 'space-around',
                            width: '100%',
                        }}
                    >
                        <PlayerCards
                            isPlayerA={true}
                            cards={bet.ResultPlayer}
                            resultView={resultView}
                            winSlot={bet.WinSlot}
                        />
                        <PlayerCards
                            isPlayerA={false}
                            cards={bet.ResultBanker}
                            resultView={resultView}
                            winSlot={bet.WinSlot}
                        />
                    </Grid>
                )}
                {resultView && bet.WinSlot != 0 && (
                    <Grid
                        container
                        sx={{
                            fontSize: '14px',
                            color: 'white',
                            width: '100%',
                        }}
                    >
                        <Grid
                            container
                            direction={'column'}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                alignSelf: 'center',
                                gap: '6px',
                            }}
                        >
                            <Grid
                                item
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center',
                                    fontSize: '18px',
                                    color: '#837254',
                                    marginTop: '5px',
                                    marginBottom: '5px',
                                }}
                            >
                                {t('teenpatti.sixCardBonus')}
                            </Grid>
                            <Grid
                                container
                                sx={{
                                    display: 'flex',
                                    direction: 'row',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                {resultView.sixCardPokers.map((card, index) => (
                                    <>{SixPoker(card, index)}</>
                                ))}
                            </Grid>
                            <Grid
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontSize: '18px',
                                    backgroundColor: '#837254',
                                    color: 'white',
                                    borderRadius: '2px',
                                    height: '28px',
                                    width: '90%',
                                    marginTop: '5px',
                                    marginBottom: '10px',
                                }}
                            >
                                {t(
                                    'teenpatti.sixCardPattern.' +
                                        resultView.sixCardPattern
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Stack>
        </Stack>
    );
};

type PlayerCardsProps = {
    isPlayerA: boolean;
    cards: Array<number>;
    resultView: ResultDisplay | undefined;
    winSlot: number;
};
const PlayerCards = (props: PlayerCardsProps) => {
    const { isPlayerA = true, cards, resultView, winSlot } = props;

    const getOpacity = (index: number) => {
        if (!resultView) return 1;
        return (isPlayerA
            ? resultView.playerALightArr
            : resultView.playerBLightArr
        ).indexOf(index) >= 0
            ? 1
            : 0.4;
    };

    const PlayerPoker = (card: number, idx: number) => {
        return (
            <Grid
                item
                key={`six-poker-${idx}`}
                sx={{
                    aspectRatio: '31/40',
                    // zoom: '0.4',
                    zoom: '0.32',
                    opacity: getOpacity(idx),
                }}
            >
                {getPokerMin(card)}
            </Grid>
        );
    };

    const getCardPatternTxt = () => {
        if (!resultView) return '';
        return t(
            'teenpatti.playerPattern.' +
                (isPlayerA
                    ? resultView.playerAPattern
                    : resultView.playerBPattern)
        );
    };

    const getBgColor = () => {
        if (!resultView) return 'black';
        if (isPlayerA && resultView.winResult == 1) return '#436FFF';
        if (!isPlayerA && resultView.winResult == 2) return '#D30000';
        return 'black';
    };

    const getBorderColor = () => {
        if (!resultView) return '#655840';
        if (isPlayerA && resultView.winResult == 1) return '#436FFF';
        if (!isPlayerA && resultView.winResult == 2) return '#D30000';
        return '#655840';
    };

    return (
        <Grid
            container
            sx={{
                direction: 'column',
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'flex-start',
                gap: '0.3rem',
                maxWidth: '38%',
            }}
        >
            <Grid
                sx={{
                    fontSize: '18px',
                    height: '25px',
                    marginBottom: '5px',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    direction: 'var(--text-direction)',
                }}
                color={isPlayerA ? '#436fff' : '#d30000'}
            >
                {t(`betlog.bet_teenpatti.${isPlayerA ? 1 : 2}`)}
            </Grid>
            <Grid
                container
                sx={{
                    direction: 'row',
                    flexWrap: 'nowrap',
                    justifyContent: 'flex-start',
                    width: '100%',
                    gap: '8px',
                }}
            >
                {cards.map((card, index) => (
                    <>{PlayerPoker(card, index)}</>
                ))}
            </Grid>
            {resultView && (
                <Grid
                    sx={{
                        marginTop: '3px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '14px',
                        color: 'white',
                        width: '100%',
                        borderRadius: '5px',
                        height: '30px',
                    }}
                    visibility={winSlot == 0 ? 'hidden' : 'visible'}
                    bgcolor={getBgColor()}
                    border={`2px solid ${getBorderColor()}`}
                >
                    {getCardPatternTxt()}
                </Grid>
            )}
        </Grid>
    );
};
